/**
 * CommentButton Component
 * Small comment icon with count badge that opens a comment thread popover.
 * Attach to any metric, P&L line, expense, or other data point.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchThread, addComment } from '@/api/commentApi';
import { formatRelativeTime } from '@/utils/formatters';
import type { CommentItem } from '@/api/commentApi';

interface CommentButtonProps {
  targetType: string;
  targetId: string;
  count?: number;
}

export function CommentButton({ targetType, targetId, count }: CommentButtonProps) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Use live comment count once loaded; fall back to prop before first open
  const displayCount = hasLoaded ? comments.length : (count ?? 0);

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const items = await fetchThread(targetType, targetId);
      setComments(items);
      setHasLoaded(true);
    } catch { /* graceful */ }
    finally { setLoading(false); }
  }, [targetType, targetId]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    loadComments();
  }, [loadComments]);

  const handleSubmit = useCallback(async () => {
    if (!newText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const comment = await addComment(targetType, targetId, newText.trim());
      setComments((prev) => [...prev, comment]);
      setNewText('');
    } catch { /* graceful */ }
    finally { setSubmitting(false); }
  }, [targetType, targetId, newText, submitting]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <span className="comment-btn" ref={ref}>
      <button
        className="comment-btn__trigger"
        onClick={handleOpen}
        aria-label={`${displayCount} comments on ${targetId}`}
        title="View comments"
        type="button"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {displayCount > 0 && (
          <span className="comment-btn__badge">{displayCount}</span>
        )}
      </button>

      {open && (
        <div className="comment-btn__popover">
          <div className="comment-btn__header">
            <span className="comment-btn__title">Comments</span>
            <button
              className="comment-btn__close"
              onClick={() => setOpen(false)}
              type="button"
            >
              &times;
            </button>
          </div>

          <div className="comment-btn__body">
            {loading && <p className="comment-btn__loading">Loading...</p>}
            {!loading && comments.length === 0 && (
              <p className="comment-btn__empty">No comments yet</p>
            )}
            {comments.map((c) => (
              <div key={c.id} className="comment-btn__item">
                <div className="comment-btn__meta">
                  <strong>{c.authorName}</strong>
                  <span>{formatRelativeTime(c.createdAt)}</span>
                </div>
                <p className="comment-btn__content">{c.content}</p>
              </div>
            ))}
          </div>

          <div className="comment-btn__input">
            <textarea
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="Add a comment..."
              rows={2}
              className="comment-btn__textarea"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <button
              onClick={handleSubmit}
              disabled={!newText.trim() || submitting}
              className="comment-btn__submit"
              type="button"
            >
              {submitting ? '...' : 'Post'}
            </button>
          </div>
        </div>
      )}
    </span>
  );
}
